import * as React from "react";
import {clsx} from "clsx";
import {parseIntegerNumber} from "@/shared/utils/datefunctions";
import {useDraftInput} from "@/shared/ui/primitives/useDraftInput";

interface NumberInputProps {
	id?: string;
	name?: string;
	/** Current numeric value; non-digit characters are stripped on commit, so only non-negative integers round-trip. */
	value: number;
	/** Fired on blur, only when the parsed draft differs from {@link NumberInputProps.value}. */
	onValueChange: (value: number) => void;
	ariaLabel?: string;
	className?: string;
	disabled?: boolean;
	maxLength?: number;
}

export function parseNumberInputValue(value: string | null): number {
	return parseIntegerNumber((value ?? "").replace(/\D/g, ""));
}

/**
 * Text-based non-negative integer input with local draft state and blur-time normalization; see
 * {@link useDraftInput} for the draft/commit semantics.
 */
export function NumberInput({
	id,
	name,
	value,
	onValueChange,
	ariaLabel,
	className,
	disabled = false,
	maxLength
}: Readonly<NumberInputProps>) {
	const draftInputProps = useDraftInput({
		value,
		format: String,
		parse: parseNumberInputValue,
		onValueChange
	});

	return (
		<input
			id={id}
			type="text"
			name={name}
			className={clsx("form-control", className)}
			aria-label={ariaLabel}
			disabled={disabled}
			maxLength={maxLength}
			{...draftInputProps}
		/>
	);
}

export type {NumberInputProps};
